import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';

import { defaultSimulatorState } from '@/domain/defaults';
import type { SimulationRecord } from '@/domain/serverTypes';
import { useAuth } from '@/hooks/useAuth';
import { pb } from '@/lib/pocketbase';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function SimulationsListPage() {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const [simulations, setSimulations] = useState<SimulationRecord[] | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SimulationRecord | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const records = await pb
          .collection('simulations')
          .getFullList<SimulationRecord>({ sort: '-updated' });
        setSimulations(records);
      } catch {
        toast.error('Impossible de charger vos simulations.');
        setSimulations([]);
      }
    })();
  }, []);

  const handleCreate = async () => {
    if (!user) return;
    setIsCreating(true);
    try {
      const record = await pb.collection('simulations').create<SimulationRecord>({
        owner: user.id,
        teams: [],
        title: defaultSimulatorState.info?.title ?? 'Sans titre',
        state: defaultSimulatorState,
        publicEnabled: false,
        publicSlug: null,
      });
      navigate(`/s/${record.id}`);
    } catch {
      toast.error('Impossible de créer une nouvelle simulation.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await pb.collection('simulations').delete(pendingDelete.id);
      setSimulations((prev) => prev?.filter((s) => s.id !== pendingDelete.id) ?? null);
      toast.success('Simulation supprimée.');
    } catch {
      toast.error('Impossible de supprimer cette simulation.');
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <AppShell>
      <main className="flex flex-col gap-6 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold tracking-tight">Mes simulations</h2>
          <Button size="sm" onClick={handleCreate} disabled={isCreating}>
            <Plus />
            Nouvelle simulation
          </Button>
        </div>

        {simulations === null && <p className="text-sm text-muted-foreground">Chargement…</p>}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {simulations?.map((sim) => (
            <Card
              key={sim.id}
              className="cursor-pointer transition-colors hover:border-primary"
              onClick={() => navigate(`/s/${sim.id}`)}
            >
              <CardHeader>
                <CardTitle className="truncate">{sim.title || 'Sans titre'}</CardTitle>
                <CardDescription>Modifié le {formatDate(sim.updated)}</CardDescription>
                <CardAction>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Supprimer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingDelete(sim);
                    }}
                  >
                    <Trash2 />
                  </Button>
                </CardAction>
              </CardHeader>
            </Card>
          ))}

          {simulations !== null && (
            <button
              type="button"
              onClick={handleCreate}
              disabled={isCreating}
              className="flex min-h-[132px] flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-primary/50 text-primary transition-colors hover:border-primary hover:bg-primary/5"
            >
              <Plus className="size-5" />
              <span className="text-sm font-medium">Nouvelle simulation</span>
            </button>
          )}
        </div>
      </main>

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer « {pendingDelete?.title || 'Sans titre'} » ?</DialogTitle>
            <DialogDescription>
              Cette action est irréversible. La simulation sera définitivement supprimée.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDelete(null)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
